import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { MonitorPlay, Plus, Wifi, WifiOff, PartyPopper, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useScreenFeed, GOAL_TYPES, minuteLabel, ABFA_CREST } from "@/lib/bigScreen";
import { formatMatchDate } from "@/lib/dates";
import { Card, CopyLink, SlidesManager, OperatorsManager, errMsg } from "@/components/bigscreen/ControlParts";

const cred = { withCredentials: true };
const MODES = [
  ["scoreboard", "Scoreboard"], ["lineups", "Line-ups"], ["stats", "Statistics"], ["halftime", "Half-Time"],
  ["fulltime", "Full-Time"], ["upcoming", "Upcoming Match"], ["standings", "Standings"], ["slides", "Sponsors & Slides"],
];
const STATUS_RANK = { live: 0, scheduled: 1, completed: 2 };

const ConnectionMonitor = ({ target }) => {
  const { snap, status, stale, lastMessageAt } = useScreenFeed(target);
  const [, force] = useState(0);
  useEffect(() => { const iv = setInterval(() => force((x) => x + 1), 1000); return () => clearInterval(iv); }, []);
  const ok = status === "live" && !stale;
  return (
    <div className={`flex items-center gap-2 text-xs px-3 py-2 ${ok ? "bg-[#009E60]/10 text-[#00704a]" : "bg-red-50 text-red-700"}`} data-testid="connection-monitor">
      {ok ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
      <b className="uppercase" data-testid="connection-status">{stale ? "stale" : status}</b>
      <span>· last update {lastMessageAt ? `${Math.round((Date.now() - lastMessageAt) / 1000)}s ago` : "—"}</span>
      {snap?.version && <span className="ml-auto font-mono text-gray-400">v{snap.version.slice(0, 6)}</span>}
    </div>
  );
};

const Preview = ({ url }) => (
  <div className="relative w-full bg-black" style={{ aspectRatio: "16 / 9" }} data-testid="screen-preview">
    <iframe title="Screen preview" src={url} className="absolute top-0 left-0 border-0 origin-top-left" style={{ width: 1920, height: 1080, transform: "scale(var(--s))" }}
      ref={(el) => { if (el) el.style.setProperty("--s", String(el.parentElement.clientWidth / 1920)); }} />
  </div>
);

const BigScreenControl = () => {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [screens, setScreens] = useState([]);
  const [target, setTarget] = useState("stadium");
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [slides, setSlides] = useState([]);
  const [operators, setOperators] = useState([]);
  const [goals, setGoals] = useState([]);
  const [newScreen, setNewScreen] = useState("");
  const origin = window.location.origin;

  const screen = screens.find((s) => s.screen_id === target);
  const loadScreens = () => axios.get(`${API}/big-screen/screens`, cred).then((r) => setScreens(r.data));
  const loadSlides = () => axios.get(`${API}/big-screen/slides`, cred).then((r) => setSlides(r.data));
  const loadOperators = () => isAdmin && axios.get(`${API}/big-screen/operators`, cred).then((r) => setOperators(r.data));

  useEffect(() => {
    loadScreens(); loadSlides(); loadOperators();
    Promise.all([axios.get(`${API}/matches?limit=500`), axios.get(`${API}/teams`)]).then(([m, t]) => {
      setMatches(m.data);
      setTeams(Object.fromEntries(t.data.map((x) => [x.team_id, x])));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!screen?.match_id) { setGoals([]); return; }
    axios.get(`${API}/matches/${screen.match_id}/events`).then((r) => setGoals(r.data.filter((e) => GOAL_TYPES.includes(e.type))));
  }, [screen?.match_id, screen?.updated_at]);

  const sortedMatches = useMemo(() => [...matches].sort((a, b) =>
    (STATUS_RANK[a.status] ?? 3) - (STATUS_RANK[b.status] ?? 3)
    || (a.status === "completed" ? (b.match_date || "").localeCompare(a.match_date || "") : (a.match_date || "").localeCompare(b.match_date || ""))), [matches]);
  const label = (m) => `${teams[m.home_team_id]?.name || "?"} vs ${teams[m.away_team_id]?.name || "?"} · ${formatMatchDate(m.match_date, { day: "numeric", month: "short" })}${m.match_time ? ` ${m.match_time}` : ""} · ${m.status}`;
  const current = matches.find((m) => m.match_id === screen?.match_id);
  const finished = current?.status === "completed";

  const update = async (payload, msg) => {
    try {
      const r = await axios.put(`${API}/big-screen/screens/${target}`, payload, cred);
      setScreens((list) => list.map((s) => (s.screen_id === target ? r.data : s)));
      if (msg) toast.success(msg);
    } catch (e) { toast.error(errMsg(e, "Update failed")); }
  };

  const addScreen = async () => {
    if (!newScreen.trim()) return;
    const r = await axios.post(`${API}/big-screen/screens`, { name: newScreen }, cred);
    setNewScreen("");
    await loadScreens();
    setTarget(r.data.screen_id);
  };

  const screenUrl = `${origin}/big-screen/screen/${target}`;
  const matchUrl = screen?.match_id ? `${origin}/match-centre/${screen.match_id}/big-screen` : null;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="big-screen-control">
      <header className="bg-[#09090b] text-white px-6 py-4 flex items-center gap-4">
        <img src={ABFA_CREST} alt="ABFA" className="w-10 h-10 object-contain" />
        <div>
          <h1 className="font-heading font-black uppercase text-2xl leading-none flex items-center gap-2"><MonitorPlay className="w-6 h-6 text-[#FFC72C]" /> Big Screen Control</h1>
          <p className="text-xs text-gray-400">{user?.name} · {user?.role === "admin" ? "Admin" : "Big Screen Operator"} · Controls presentation only — scores come from the official match scoring system</p>
        </div>
        <div className="ml-auto flex gap-2">
          {isAdmin && <Link to="/admin" className="px-3 py-2 text-sm bg-white/10 hover:bg-white/20" data-testid="back-to-admin">Admin</Link>}
          <button onClick={logout} className="px-3 py-2 text-sm bg-white/10 hover:bg-white/20 flex items-center gap-1" data-testid="control-logout"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto p-6 grid grid-cols-1 xl:grid-cols-[1fr_520px] gap-6">
        <div className="space-y-6 min-w-0">
          <Card title="Screen" testId="screen-card">
            <div className="flex flex-wrap gap-2 items-center">
              {screens.map((s) => (
                <button key={s.screen_id} onClick={() => setTarget(s.screen_id)} className={`px-4 py-2 text-sm font-semibold border ${target === s.screen_id ? "bg-[#09090b] text-white border-[#09090b]" : "bg-white"}`} data-testid={`screen-tab-${s.screen_id}`}>{s.name}</button>
              ))}
              <Input value={newScreen} onChange={(e) => setNewScreen(e.target.value)} placeholder="New screen name" className="w-48 rounded-none h-9" data-testid="new-screen-input" />
              <Button size="sm" variant="outline" className="rounded-none" onClick={addScreen} data-testid="add-screen-btn"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="mt-4 space-y-2">
              <CopyLink label="Stadium link" url={screenUrl} testId="stadium-link" />
              {matchUrl && <CopyLink label="Match link" url={matchUrl} testId="match-link" />}
            </div>
          </Card>

          <Card title="Match on screen" testId="match-card">
            <select value={screen?.match_id || ""} onChange={(e) => (e.target.value ? update({ match_id: e.target.value }, "Match selected") : update({ clear_match: true }, "Match cleared"))}
              className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="match-select">
              <option value="">— No match (show slides / ABFA screen) —</option>
              {sortedMatches.map((m) => <option key={m.match_id} value={m.match_id}>{label(m)}</option>)}
            </select>
          </Card>

          <Card title="Presentation mode" testId="mode-card">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {MODES.map(([k, name]) => {
                const disabled = (k === "fulltime" && !finished) || (["lineups", "stats", "halftime", "fulltime", "standings"].includes(k) && !screen?.match_id);
                return (
                  <button key={k} disabled={disabled} onClick={() => update({ mode: k }, `${name} on screen`)}
                    className={`px-3 py-4 font-heading font-bold uppercase text-sm border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${screen?.mode === k ? "bg-[#FFC72C] border-[#FFC72C] text-black" : "bg-white hover:border-gray-500"}`}
                    data-testid={`mode-${k}`}>{name}</button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-3">Scoreboard automatically shows Half-Time and Full-Time presentations at the right stage of the match.</p>
          </Card>

          <Card title="Goal replay" testId="replay-card">
            {goals.length === 0 ? <p className="text-sm text-gray-400">No goals recorded for this match.</p> : (
              <div className="flex flex-wrap gap-2">
                {goals.map((g) => (
                  <Button key={g.event_id} size="sm" variant="outline" className="rounded-none" onClick={() => update({ replay_goal_event_id: g.event_id }, "Goal replay sent")} data-testid={`replay-goal-${g.event_id}`}>
                    <PartyPopper className="w-4 h-4 mr-1" /> {g.player_name || teams[g.team_id]?.name} {minuteLabel(g)}
                  </Button>
                ))}
              </div>
            )}
          </Card>

          <Card title="Settings" testId="settings-card">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <label className="space-y-1"><span className="font-medium">GOAL! duration (s)</span>
                <Input type="number" min={3} max={60} defaultValue={screen?.celebration_seconds ?? 12} key={`c-${target}-${screen?.celebration_seconds}`} onBlur={(e) => update({ celebration_seconds: Number(e.target.value) }, "Saved")} className="rounded-none" data-testid="celebration-seconds-input" /></label>
              <label className="space-y-1"><span className="font-medium">Slide duration (s)</span>
                <Input type="number" min={3} max={120} defaultValue={screen?.slide_seconds ?? 10} key={`s-${target}-${screen?.slide_seconds}`} onBlur={(e) => update({ slide_seconds: Number(e.target.value) }, "Saved")} className="rounded-none" data-testid="slide-seconds-input" /></label>
              <label className="space-y-1"><span className="font-medium">Upcoming match</span>
                <select value={screen?.upcoming_match_id || ""} onChange={(e) => update({ upcoming_match_id: e.target.value }, "Upcoming match set")} className="w-full border border-gray-300 px-2 py-2" data-testid="upcoming-select">
                  <option value="">Auto — next scheduled fixture</option>
                  {sortedMatches.filter((m) => m.status === "scheduled").map((m) => <option key={m.match_id} value={m.match_id}>{label(m)}</option>)}
                </select></label>
            </div>
          </Card>

          <Card title="Sponsors & announcements" testId="slides-card">
            <SlidesManager slides={slides} selected={screen?.slide_ids || []} reload={loadSlides}
              onToggle={(id) => { const cur = screen?.slide_ids || []; update({ slide_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] }); }} />
          </Card>

          {isAdmin && (
            <Card title="Operator accounts" testId="operators-card">
              <OperatorsManager operators={operators} reload={loadOperators} />
            </Card>
          )}
        </div>

        <div className="space-y-3 xl:sticky xl:top-6 self-start">
          <Card title="Live preview" testId="preview-card" right={<span className="text-xs text-gray-500">{screen?.name}</span>}>
            <Preview url={`/big-screen/screen/${target}`} key={target} />
            <div className="mt-3"><ConnectionMonitor target={target} /></div>
            <p className="text-xs text-gray-500 mt-3">On the stadium PC open the Stadium link in Chrome, click <b>Fullscreen</b> (or press F11). It follows whatever you select here.</p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BigScreenControl;
